import React, { useState, useEffect, useRef } from 'react';
import './Speakers.css';
import profile from '../images/profile2.png';
import { speakersService } from '../services/speakersService';
import { conferencesService } from '../services/conferencesService';
import { supabase } from '../supabaseClient';
import SpeakersOnline from './SpeakersOnline';

function Speakers({ edition: customEdition }) {
  const [speakersData, setSpeakersData] = useState({
    speakersHeader: { title: 'Nos Intervenants' },
    speakersIntro: { text: '' },
    speakersSection: { title: 'Intervenants Confirmés' },
    displayMode: 'grouped',
    speakers: [],
    laboratories: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [speakersPerSlide, setSpeakersPerSlide] = useState(4);
  const [expanded, setExpanded] = useState({});
  const [expandedLabs, setExpandedLabs] = useState({});
  const infoRefs = useRef([]);

  // Track max height for current slide's info boxes
  const [maxInfoHeight, setMaxInfoHeight] = useState(0);

  const [conferences, setConferences] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState('');
  const [conferenceSpeakers, setConferenceSpeakers] = useState([]);
  const [conferenceTitle, setConferenceTitle] = useState('');
  const [showConferenceFilter, setShowConferenceFilter] = useState(false);
  const [conferenceInfo, setConferenceInfo] = useState(null);

  useEffect(() => {
    // Responsive speakers per slide
    const handleResize = () => {
      if (window.innerWidth <= 480) {
        setSpeakersPerSlide(4);
      } else {
        setSpeakersPerSlide(4);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadSpeakersData();
    loadConferences();
  }, [customEdition]);

  const loadSpeakersData = async () => {
    try {
      setLoading(true);

      let editionYear = customEdition;
      if (!editionYear) {
        // Get active edition first
        const { data: activeEdition } = await supabase
          .from('editions')
          .select('year')
          .eq('is_active', true)
          .single();

        editionYear = activeEdition?.year || '2025';
      }

      const data = await speakersService.getSpeakersData(editionYear);

      if (data) {
        setSpeakersData({
          speakersHeader: data.speakersHeader,
          speakersIntro: data.speakersIntro,
          speakersSection: data.speakersSection,
          displayMode: data.displayMode || 'grouped',
          categoryInternationalOrder: data.categoryInternationalOrder ?? 1,
          categoryMarocainOrder: data.categoryMarocainOrder ?? 2,
          speakers: data.speakers,
          onlineSpeakers: data.onlineSpeakers || [],
          laboratories: data.laboratories || []
        });
      }
      setError(null);
    } catch (error) {
      setError('Failed to load speakers data');
    } finally {
      setLoading(false);
    }
  };

  const loadConferences = async () => {
    try {
      let editionYear = customEdition;
      if (!editionYear) {
        const { data: activeEdition } = await supabase
          .from('editions')
          .select('year')
          .eq('is_active', true)
          .single();
        editionYear = activeEdition?.year || '2025';
      }

      const data = await conferencesService.getConferencesData(editionYear);
      setConferences(data.conferences || []);
    } catch (error) {
      setConferences([]);
    }
  };

  // When conference or speakersData changes, update filtered speakers and info
  useEffect(() => {
    if (!selectedConferenceId) {
      setConferenceSpeakers([]);
      setConferenceTitle('');
      setConferenceInfo(null);
      return;
    }
    const conf = conferences.find(c => String(c.id) === String(selectedConferenceId));
    setConferenceTitle(conf ? conf.titre : '');
    setConferenceInfo(conf || null);
    // Find speakers linked to this conference
    const speakerIds = (conf?.speakers || []).map(String);
    setConferenceSpeakers(
      speakersData.speakers
        .filter(s => speakerIds.includes(String(s.id)))
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
    );
  }, [selectedConferenceId, conferences, speakersData.speakers]);

  // Reset carousel slide when filtered speakers change
  useEffect(() => {
    setExpanded({});
  }, [selectedConferenceId, conferenceSpeakers, speakersData.speakers]);

  // Update max height for info boxes on speakers change
  useEffect(() => {
    if (!infoRefs.current) return;
    const heights = infoRefs.current.map(ref => ref ? ref.offsetHeight : 0);
    setMaxInfoHeight(Math.max(...heights, 0));
  }, [speakersData.speakers, conferenceSpeakers]);

  // Toggle expand/collapse for a speaker
  const toggleExpand = (id) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Helper to truncate text and add "Voir plus/moins"
  const renderExpandableText = (text, id, maxLength = 60) => {
    if (!text) return null;
    if (text.length <= maxLength) return <span>{text}</span>;
    const isOpen = expanded[id];
    return (
      <>
        <span>
          {isOpen ? text : text.slice(0, maxLength) + '...'}
        </span>
        <button
          className="voir-plus-btn"
          onClick={() => toggleExpand(id)}
          type="button"
        >
          {isOpen ? 'Voir moins' : 'Voir plus'}
        </button>
      </>
    );
  };

  const toggleLabDetails = (labKey) => {
    setExpandedLabs(prev => ({
      ...prev,
      [labKey]: !prev[labKey]
    }));
  };

  const escapeHtml = (unsafe) => {
    if (!unsafe && unsafe !== 0) return '';
    return String(unsafe)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
      .replace(/\n/g, '<br/>');
  };

  const buildPrintHtml = (group, index) => {
    const lab = group.lab || {};
    const sections = [
      { title: 'Présidents du laboratoire', speakers: group.presidents },
      { title: 'Experts du laboratoire', speakers: group.experts || [...(group.internationalExperts || []), ...(group.academicExperts || [])].sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999)) },
      { title: 'Experts sécurité', speakers: group.securityExperts }
    ].filter(section => section.speakers && section.speakers.length > 0);

    const formatList = (text) => {
      const lines = escapeHtml(text).split(/<br\/>|\n+/).map(line => line.trim()).filter(Boolean);
      if (lines.length === 1) {
        return `<p class="print-text">${lines[0]}</p>`;
      }
      return `<ul class="print-list">${lines.map(line => `<li>${line}</li>`).join('')}</ul>`;
    };

    const speakerCardsHtml = sections.map(section => `
      <section class="print-speaker-section">
        <h2>${escapeHtml(section.title)}</h2>
        <div class="print-speaker-grid">
          ${section.speakers.map(speaker => {
            const imageUrl = escapeHtml(speaker.image || profile);
            const companyLogo = speaker.companyLogo ? `<img src="${escapeHtml(speaker.companyLogo)}" class="print-company-logo" alt="Logo" />` : '';
            const onlineTag = Boolean(speakersData.onlineSpeakers?.find(os => os.id === speaker.id))
              ? '<span class="print-online-tag">ONLINE</span>'
              : '';
            return `
              <article class="print-speaker-card">
                <div class="print-speaker-media">
                  <div class="print-speaker-photo">
                    <img src="${imageUrl}" alt="${escapeHtml(speaker.name)}" />
                    ${onlineTag}
                  </div>
                  ${companyLogo}
                </div>
                <div class="print-speaker-content">
                  <h3>${escapeHtml(speaker.name)}</h3>
                  ${speaker.title ? `<p class="print-speaker-role">${escapeHtml(speaker.title)}</p>` : ''}
                  ${speaker.organization ? `<p class="print-speaker-org">${escapeHtml(speaker.organization)}</p>` : ''}
                  ${speaker.roleLabel ? `<p class="print-speaker-meta">${escapeHtml(speaker.roleLabel)}</p>` : speaker.role ? `<p class="print-speaker-meta">${escapeHtml(speaker.role)}</p>` : ''}
                </div>
              </article>
            `;
          }).join('')}
        </div>
      </section>
    `).join('');

    return `<!DOCTYPE html>
      <html lang="fr">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <base href="${window.location.origin}/" />
        <title>${lab.title || 'Laboratoire'}</title>
        <style>
          @page { size: A4 portrait; margin: 20mm; }
          body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; color: #111; background: #fff; }
          .page { width: 210mm; min-height: 297mm; padding: 20mm; box-sizing: border-box; }
          .print-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; margin-bottom: 1rem; }
          .print-header h1 { margin: 0; font-size: 2rem; color: #002b5b; }
          .print-header .print-lab-tag { display: inline-block; background: #c20e0f; color: #fff; padding: 0.35rem 0.75rem; border-radius: 999px; font-size: 0.95rem; letter-spacing: 0.08em; text-transform: uppercase; }
          .print-subtitle { margin: 0.5rem 0 1.5rem; font-size: 1rem; color: #4b5563; }
          .print-section { margin-bottom: 1.5rem; }
          .print-section h2 { margin: 0 0 0.8rem; font-size: 1.1rem; color: #002b5b; }
          .print-text { margin: 0.4rem 0; line-height: 1.6; color: #333; }
          .print-speaker-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; }
          .print-speaker-card { background: #f8fafc; border: 1px solid #d1d5db; border-radius: 0.9rem; padding: 1rem; display: flex; gap: 1rem; align-items: flex-start; }
          .print-speaker-media { display: flex; flex-direction: column; gap: 0.5rem; }
          .print-speaker-photo { position: relative; width: 5.5rem; min-width: 5.5rem; height: 5.5rem; border-radius: 1rem; overflow: hidden; }
          .print-speaker-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
          .print-online-tag { position: absolute; bottom: 0.5rem; left: 0.5rem; background: rgba(0, 43, 91, 0.9); color: #fff; padding: 0.25rem 0.5rem; border-radius: 999px; font-size: 0.65rem; letter-spacing: 0.05em; text-transform: uppercase; }
          .print-company-logo { width: 100%; max-width: 5.5rem; border-radius: 0.75rem; }
          .print-speaker-content { flex: 1; display: flex; flex-direction: column; gap: 0.35rem; }
          .print-speaker-card h3 { margin: 0 0 0.35rem; font-size: 1rem; color: #002b5b; }
          .print-speaker-role, .print-speaker-org, .print-speaker-meta { margin: 0.15rem 0; font-size: 0.9rem; color: #374151; }
          .print-detail-grid { display: grid; grid-template-columns: 1fr; gap: 1rem; }
          .print-detail-block { padding: 1rem; border: 1px solid #d1d5db; border-radius: 0.9rem; background: #f9fafb; }
          .print-detail-block h2 { margin: 0 0 0.5rem; font-size: 1rem; color: #002b5b; }
          .print-list { margin: 0; padding-left: 1.2rem; color: #333; }
          .print-list li { margin: 0.35rem 0; line-height: 1.5; }
          .print-footer { margin-top: 2rem; font-size: 0.85rem; color: #6b7280; }
          @media print { body { background: #fff; } .page { box-shadow: none; } }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="print-header">
            <div>
              <div class="print-lab-tag">LAB ${index + 1}</div>
              <h1>${lab.title || 'Laboratoire'}</h1>
              ${lab.subtitle ? `<p class="print-subtitle">${lab.subtitle}</p>` : ''}
            </div>
          </div>
          <div class="print-detail-grid">
            ${lab.description ? `<div class="print-detail-block"><h2>Description</h2><p class="print-text">${lab.description}</p></div>` : ''}
            ${lab.objectives ? `<div class="print-detail-block"><h2>Objectifs</h2>${formatList(lab.objectives)}</div>` : ''}
            ${lab.themes ? `<div class="print-detail-block"><h2>Principales thématiques</h2>${formatList(lab.themes)}</div>` : ''}
            ${lab.value ? `<div class="print-detail-block"><h2>Valeur ajoutée</h2>${formatList(lab.value)}</div>` : ''}
          </div>
          ${speakerCardsHtml}
          <div class="print-footer">Imprimé depuis l'application CIGSIDA</div>
        </div>
      </body>
      </html>`;
  };

  const printLabDetails = (group, index) => {
    const printWindow = window.open('', '_blank', 'top=0,left=0,height=900,width=900');
    if (!printWindow) {
      alert('Impossible d’ouvrir la fenêtre d’impression. Vérifiez votre bloqueur de pop-ups.');
      return;
    }

    const html = buildPrintHtml(group, index);
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();

    const tryPrint = () => {
      if (printWindow.document.readyState === 'complete') {
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      } else {
        setTimeout(tryPrint, 200);
      }
    };

    tryPrint();
  };

  const normalizeLaboratoryInfo = (speaker) => {
    const labData = speaker?.laboratory || speaker?.lab || {};
    return {
      title: speaker?.laboratoryTitle || speaker?.labTitle || labData?.title || speaker?.organization || 'Laboratoire',
      subtitle: speaker?.laboratorySubtitle || speaker?.labSubtitle || labData?.subtitle || '',
      description: speaker?.laboratoryDescription || speaker?.labDescription || labData?.description || '',
      objectives: speaker?.laboratoryObjectives || speaker?.labObjectives || labData?.objectives || '',
      themes: speaker?.laboratoryThemes || speaker?.labThemes || labData?.themes || '',
      value: speaker?.laboratoryValue || speaker?.labValue || labData?.value || ''
    };
  };

  const getSpeakerRoleSection = (speaker, assignment = null) => {
    const role = `${assignment?.role || speaker?.role || speaker?.membershipRole || ''}`.toLowerCase();
    const roleLabel = `${assignment?.roleLabel || speaker?.roleLabel || speaker?.position || ''}`.toLowerCase();

    if (speaker?.isPresident || role === 'president' || role.includes('president') || roleLabel.includes('président') || roleLabel.includes('president')) {
      return 'presidents';
    }

    if (
      role === 'security_expert' ||
      role.includes('security') ||
      role.includes('securite') ||
      role.includes('sécurité') ||
      roleLabel.includes('security') ||
      roleLabel.includes('securite') ||
      roleLabel.includes('sécurité')
    ) {
      return 'securityExperts';
    }

    if (
      role === 'academic_expert' ||
      speaker?.category === 'academic' ||
      speaker?.category === 'academique' ||
      speaker?.expertiseType === 'academic' ||
      speaker?.isAcademic ||
      role.includes('academic') ||
      role.includes('académique') ||
      roleLabel.includes('académique') ||
      roleLabel.includes('academic') ||
      speaker?.mainCategory === 'marocain'
    ) {
      return 'academicExperts';
    }

    if (
      role === 'international_expert' ||
      role.includes('international') ||
      roleLabel.includes('international') ||
      speaker?.mainCategory === 'international'
    ) {
      return 'internationalExperts';
    }

    return 'internationalExperts';
  };

  const getSpeakersGroupedByLaboratoire = () => {
    const allRegularSpeakers = selectedConferenceId ? conferenceSpeakers : speakersData.speakers;
    const allOnlineSpeakers = speakersData.onlineSpeakers || [];
    const allSpeakers = [...allRegularSpeakers, ...allOnlineSpeakers];

    const grouped = new Map();

    (speakersData.laboratories || []).forEach((lab) => {
      const labKey = String(lab.id);
      let partnerLogos = [];
      if (lab.partner_logos) {
        try {
          partnerLogos = JSON.parse(lab.partner_logos);
          if (!Array.isArray(partnerLogos)) {
            partnerLogos = [];
          }
        } catch {
          partnerLogos = String(lab.partner_logos)
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        }
      }

      grouped.set(labKey, {
        id: labKey,
        lab: {
          title: lab.title || '',
          subtitle: lab.subtitle || '',
          description: lab.description || '',
          objectives: lab.objectives || '',
          themes: lab.themes || '',
          value: lab.value || '',
          partnerLogos,
          partnerDescription: lab.partner_description || '',
          pdfUrl: lab.pdf_url || lab.pdfUrl || ''
        },
        presidents: [],
        internationalExperts: [],
        academicExperts: [],
        securityExperts: [],
        displayOrder: lab.displayOrder ?? lab.display_order ?? (Number(lab.id) || 999)
      });
    });

    allSpeakers.forEach((speaker) => {
      const assignments = speaker.laboratoryAssignments && speaker.laboratoryAssignments.length > 0
        ? speaker.laboratoryAssignments
        : speaker.laboratoryId
          ? [{ laboratory_id: speaker.laboratoryId, role: speaker.role, roleLabel: speaker.roleLabel, display_order: speaker.displayOrderInLab }]
          : [];

      assignments.forEach((assignment) => {
        const labKey = String(assignment.laboratory_id || assignment.laboratoryId || assignment.lab?.id || '');

        if (!labKey || !grouped.has(labKey)) {
          return;
        }

        const entry = grouped.get(labKey);
        const section = getSpeakerRoleSection(speaker, assignment);

        if (!entry[section].some((s) => String(s.id) === String(speaker.id))) {
          entry[section].push({
            ...speaker,
            role: assignment.role || speaker.role,
            roleLabel: assignment.roleLabel || speaker.roleLabel,
            displayOrder: assignment.display_order ?? speaker.displayOrderInLab ?? speaker.displayOrder ?? 999
          });
        }
      });
    });

    return Array.from(grouped.values())
      .map((group) => {
        const sortedIntl = group.internationalExperts.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
        const sortedAcad = group.academicExperts.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
        const combinedExperts = [...sortedIntl, ...sortedAcad].sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
        return {
          ...group,
          presidents: group.presidents.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999)),
          internationalExperts: sortedIntl,
          academicExperts: sortedAcad,
          experts: combinedExperts,
          securityExperts: group.securityExperts.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999))
        };
      })
      .sort((a, b) => {
        const orderA = Number(a.displayOrder ?? a.id ?? 999);
        const orderB = Number(b.displayOrder ?? b.id ?? 999);
        if (orderA !== orderB) return orderA - orderB;
        return (a.lab?.title || '').localeCompare(b.lab?.title || '', 'fr', { sensitivity: 'base' });
      });
  };

  const renderLaboratoryDetailContent = (text) => {
    if (!text) return null;

    const lines = `${text}`
      .split(/\n+/)
      .map(item => item.trim())
      .filter(Boolean);

    if (lines.length <= 1) {
      return <p className="laboratory-detail-text">{text}</p>;
    }

    return (
      <ul className="laboratory-detail-list">
        {lines.map((item, index) => (
          <li key={`${item}-${index}`} className="laboratory-detail-list-item">{item}</li>
        ))}
      </ul>
    );
  };

  const renderSpeakerCard = (speaker) => {
    const isOnlineSpeaker = Boolean(speakersData.onlineSpeakers?.find(os => os.id === speaker.id));

    return (
      <div key={speaker.id} className="speaker-member">
        <div className="member-photo">
          {speaker.companyLogo && (
            <div className="company-logo">
              <img
                src={speaker.companyLogo}
                alt="Company Logo"
                onError={(e) => (e.target.style.display = 'none')}
              />
            </div>
          )}
          <img src={speaker.image || profile} alt={speaker.name} />
          {/* <div className="country-flag">
            <img src={speaker.flagUrl} alt="flag" />
          </div> */}
          {isOnlineSpeaker && (
            <div className="online-tag">
              <span>ONLINE</span>
            </div>
          )}
        </div>
        <div className="member-infospeaker">
          <h4>{speaker.name}</h4>
          <p>
            {renderExpandableText(speaker.title, `${speaker.id}-title`)}
          </p>
          <p>
            {renderExpandableText(speaker.organization, `${speaker.id}-org`)}
          </p>
        </div>
      </div>
    );
  };

  // New function to group speakers by cadre or conference
  const getSpeakersGroupedByConference = () => {
    const grouped = {};
    const unassignedSpeakers = [];

    // Get all speakers including online speakers
    const allRegularSpeakers = selectedConferenceId ? conferenceSpeakers : speakersData.speakers;
    const allOnlineSpeakers = speakersData.onlineSpeakers || [];
    const allSpeakers = [...allRegularSpeakers, ...allOnlineSpeakers];

    if (selectedConferenceId) {
      // If filtering by specific conference, group by conference as before
      const conf = conferences.find(c => String(c.id) === String(selectedConferenceId));
      if (conf) {
        // Filter online speakers that might be linked to this conference
        const filteredOnlineSpeakers = allOnlineSpeakers.filter(speaker => 
          speaker.cadres && speaker.cadres.some(cadre => 
            conferences.some(c => c.titre === cadre.title && String(c.id) === String(selectedConferenceId))
          )
        );
        grouped[conf.id] = {
          conference: conf,
          speakers: [...conferenceSpeakers, ...filteredOnlineSpeakers],
          displayOrder: 1
        };
      }
    } else if (speakersData.displayMode === 'by_category') {
      // Group speakers by main category (International vs Marocain)
      const internationalGroup = {
        conference: { titre: 'INTERVENANTS UNIVERSITAIRES INTERNATIONAUX', id: 'international' },
        speakers: [],
        displayOrder: speakersData.categoryInternationalOrder || 1
      };
      const marocainGroup = {
        conference: { titre: 'INTERVENANTS UNIVERSITAIRES MAROCAINS', id: 'marocain' },
        speakers: [],
        displayOrder: speakersData.categoryMarocainOrder || 2
      };

      allSpeakers.forEach(speaker => {
        if (speaker.mainCategory === 'marocain') {
          marocainGroup.speakers.push(speaker);
        } else {
          // Default to international if not specified or explicitly 'international'
          internationalGroup.speakers.push(speaker);
        }
      });

      // Sort speakers within groups by displayOrder
      internationalGroup.speakers.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
      marocainGroup.speakers.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));

      if (internationalGroup.speakers.length > 0) grouped['international'] = internationalGroup;
      if (marocainGroup.speakers.length > 0) grouped['marocain'] = marocainGroup;

    } else {
      // Group speakers by their cadre_conference titles
      allSpeakers.forEach(speaker => {
        if (speaker.cadres && speaker.cadres.length > 0) {
          // Add speaker to each of their cadre groups
          speaker.cadres.forEach(cadre => {
            if (!grouped[cadre.title]) {
              grouped[cadre.title] = {
                conference: { 
                  titre: cadre.title, 
                  id: cadre.title,
                  description: '',
                  dateDebut: '',
                  heureDebut: '',
                  heureFin: '',
                  salle: ''
                },
                speakers: [],
                displayOrder: cadre.display_order || 999 
              };
            }
            // Only add if not already in this group
            if (!grouped[cadre.title].speakers.find(s => s.id === speaker.id)) {
              // Add speaker with their order in this specific cadre
              grouped[cadre.title].speakers.push({
                ...speaker,
                speakerOrderInCadre: cadre.speaker_order || 999
              });
            }
          });
        } else {
          unassignedSpeakers.push({
            ...speaker,
            speakerOrderInCadre: speaker.displayOrder || 999
          });
        }
      });

      // Sort speakers within each cadre by their speaker_order
      Object.keys(grouped).forEach(cadreTitle => {
        grouped[cadreTitle].speakers.sort((a, b) => {
          // Primary sort by order within the specific cadre
          const cadreOrderA = a.speakerOrderInCadre || 999;
          const cadreOrderB = b.speakerOrderInCadre || 999;
          
          if (cadreOrderA !== cadreOrderB) {
            return cadreOrderA - cadreOrderB;
          }
          
          // Fallback to the global display order if cadre order is same
          return (a.displayOrder || 0) - (b.displayOrder || 0);
        });
      });

      // Add unassigned speakers group if any exist
      if (unassignedSpeakers.length > 0) {
        // Sort unassigned speakers by their general display order
        unassignedSpeakers.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
        grouped['unassigned'] = {
          conference: { titre: 'Autres Intervenants', id: 'unassigned' },
          speakers: unassignedSpeakers,
          displayOrder: 1000 // Always show at the end
        };
      }
    }

    // Sort grouped entries by display order
    const sortedEntries = Object.entries(grouped).sort(([, a], [, b]) => {
      return (a.displayOrder || 999) - (b.displayOrder || 999);
    });

    return Object.fromEntries(sortedEntries);
  };

  const getAllSpeakers = () => {
    const regularSpeakers = selectedConferenceId ? conferenceSpeakers : speakersData.speakers;
    const onlineSpeakers = speakersData.onlineSpeakers || [];
    const all = [...regularSpeakers, ...onlineSpeakers];
    return all.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  };

  const shouldShowLaboratoryView =
    speakersData.displayMode === 'speakersgroupedbyLaboratoire' ||
    speakersData.displayMode === 'grouped_by_laboratory' ||
    (speakersData.laboratories || []).length > 0;

  const laboratoryGroups = getSpeakersGroupedByLaboratoire();

  const hasVisibleContent = shouldShowLaboratoryView
    ? laboratoryGroups.length > 0
    : Object.keys(getSpeakersGroupedByConference()).length > 0;

  if (loading) {
    return (
      <div className="speakers-page">
        <div className="speakers-container">
          <div className="speakers-header">
              <div className="loading-spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="speakers-page">
      <div className="speakers-container">
        <div className="speakers-header">
          <h1>
            {speakersData.speakersHeader.title}
          </h1>
          
        </div>

        <div className="speakers-content">
          <div className="speakers-overview">
          
            <div className="speakers-intro">
              <p className="intro-text">
                {selectedConferenceId
                  ? conferenceTitle
                  : speakersData.speakersIntro.text}
              </p>
            </div>
            <div className="congress-speakers">
              <h2>
                {selectedConferenceId
                  ? conferenceTitle
                  : speakersData.speakersSection.title}
              </h2>

                {/* Conference filter button and dropdown */}
            <div style={{ marginBottom: 24 }}>
              {!showConferenceFilter ? (
                <button
                  className="btn-secondary"
                  style={{ marginBottom: 8 }}
                  onClick={() => setShowConferenceFilter(true)}
                >
                  Filtrer par conférence
                </button>
              ) : (
                <div className="form-group" style={{ marginBottom: 8 }}>
                  <label style={{ marginRight: 8 }}>Conférence :</label>
                  <select
                    value={selectedConferenceId}
                    onChange={e => setSelectedConferenceId(e.target.value)}
                    style={{ minWidth: 200, marginRight: 8 }}
                  >
                    <option value="">Toutes les conférences</option>
                    {conferences.map(conf => (
                      <option key={conf.id} value={conf.id}>
                        {conf.titre}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn-secondary"
                    style={{  width: "100px" , textAlign: 'center' }}
                    onClick={() => {
                      setShowConferenceFilter(false);
                      setSelectedConferenceId('');
                    }}
                  >
                    Annuler
                  </button>
                </div>
              )}
            </div>
            
              {hasVisibleContent ? (
                <>
                  <div className="speakers-by-conference">
                    {(speakersData.displayMode === 'flat' && !selectedConferenceId) ? (
                      <div className="speakers-grid">
                        {getAllSpeakers().map((speaker) => renderSpeakerCard(speaker))}
                      </div>
                    ) : shouldShowLaboratoryView ? (
                      <div className="laboratory-groups">
                        {laboratoryGroups.map((group, index) => {
                          const isDetailsOpen = expandedLabs[group.id];
                          const hasDetails = Boolean(
                            (group.lab.description && group.lab.description.trim()) ||
                            (group.lab.objectives && group.lab.objectives.trim()) ||
                            (group.lab.themes && group.lab.themes.trim()) ||
                            (group.lab.value && group.lab.value.trim())
                          );

                          return (
                            <div key={group.id} className="laboratory-group">
                              <div className="laboratory-group-header">
                                <div>
                                  <h3 className="laboratory-group-title">
                                    <span className="laboratory-number-tag">LAB  {index + 1}</span>
                                   
                                  </h3> 
                                  {group.lab.subtitle && (
                                    <p className="laboratory-group-subtitle">{group.lab.subtitle}</p>
                                  )}
                                </div>
                              <div className="laboratory-header-actions">
                                {hasDetails && (
                                  <button
                                    type="button"
                                    className="laboratory-detail-toggle"
                                    onClick={() => toggleLabDetails(group.id)}
                                  >
                                    {isDetailsOpen ? 'Masquer le détail' : 'Détails du laboratoire'}
                                    <i
                                      className={`pi ${isDetailsOpen ? 'pi-chevron-up' : 'pi-chevron-down'}`}
                                      style={{ marginLeft: '1rem' }}
                                    ></i>
                                  </button>
                                )}
                                {group.lab.pdfUrl && (
                                  <a
                                    href={group.lab.pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="laboratory-pdf-btn"
                                    title="Ouvrir le document PDF"
                                  >
                                    Détails en PDF
                                    <i className="pi pi-file-pdf" style={{ marginLeft: '1rem' }}></i>
                                  </a>
                                )}
                                <button
                                  type="button"
                                  className="laboratory-print-btn"
                                  onClick={() => printLabDetails(group, index)}
                                >
                                  Imprimer
                                  <i className="pi pi-print" style={{ marginLeft: '1rem' }}></i>
                                </button>
                              </div>
                            </div>
                              <span className="laboratory-group-name">{group.lab.title}</span>

                              {isDetailsOpen && (
                                <div className="laboratory-details-panel">
                                  {group.lab.description && (
                                    <div className="laboratory-detail-block">
                                      <h4 className="laboratory-detail-label">Description</h4>
                                      <p className="laboratory-detail-text">{group.lab.description}</p>
                                    </div>
                                  )}
                                  {group.lab.objectives && (
                                    <div className="laboratory-detail-block">
                                      <h4 className="laboratory-detail-label">Objectifs</h4>
                                      {renderLaboratoryDetailContent(group.lab.objectives)}
                                    </div>
                                  )}
                                  {group.lab.themes && (
                                    <div className="laboratory-detail-block">
                                      <h4 className="laboratory-detail-label">Principales thématiques</h4>
                                      {renderLaboratoryDetailContent(group.lab.themes)}
                                    </div>
                                  )}
                                  {group.lab.value && (
                                    <div className="laboratory-detail-block">
                                      <h4 className="laboratory-detail-label">Valeur ajoutée</h4>
                                      <p className="laboratory-detail-text">{group.lab.value}</p>
                                    </div>
                                  )}
                                </div>
                              )}

                              {group.presidents.length > 0 && (
                                 <div className="laboratory-speaker-section">
                                   <h4 className="laboratory-speaker-section-title">Présidents du laboratoire</h4>
                                   <div className="laboratory-speaker-grid">
                                     {group.presidents.map((speaker) => renderSpeakerCard(speaker))}
                                   </div>
                                 </div>
                               )}

                               {group.experts.length > 0 && (
                                 <div className="laboratory-speaker-section">
                                   <h4 className="laboratory-speaker-section-title">Experts du laboratoire</h4>
                                   <div className="laboratory-speaker-grid">
                                     {group.experts.map((speaker) => renderSpeakerCard(speaker))}
                                   </div>
                                 </div>
                               )}

                              {(group.lab.partnerLogos?.length > 0 || group.lab.partnerDescription) && (
                                <div className="laboratory-partners">
                                  <div className="">
                                    <h4 className="laboratory-speaker-section-title">Partenaires & établissements</h4>
                                    {group.lab.partnerDescription && (
                                      <p className="laboratory-partner-description">{group.lab.partnerDescription}</p>
                                    )}
                                  </div>
                                  <div className="laboratory-partner-logos">
                                    {group.lab.partnerLogos.map((logo, index) => (
                                      <div key={index} className="laboratory-partner-logo">
                                        <img src={logo} alt={`Partenaire ${index + 1}`} />
                                      </div>     ))}
                                      <div>
                                      <p className="laboratory-partner-description">+ autres Partenaires</p></div>
                               
                                  </div>
                                </div>
                              )}

                              {group.securityExperts.length > 0 && (
                                <div className="laboratory-speaker-section">
                                  {/* <h4 className="laboratory-speaker-section-title">Experts sécurité</h4> */}
                                  <div className="laboratory-speaker-grid">
                                    {group.securityExperts.map((speaker) => renderSpeakerCard(speaker))}
                                  </div>
                                </div>
                              )}

                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      Object.entries(getSpeakersGroupedByConference()).map(([confId, group]) => (
                        <div key={confId} className="conference-group">
                          <div className="conference-group-header">
                            <h3 className="conference-group-title">{group.conference.titre}</h3>
                            {group.conference.id !== 'unassigned' && selectedConferenceId && (
                              <div className="conference-group-info">
                                {group.conference.description && group.conference.description.trim().toLowerCase() !== 'aucune' && (
                                  <div className="conference-description">{group.conference.description}</div>
                                )}
                                <div className="conference-details">
                                  <span className="conference-date">
                                    {group.conference.dateDebut}
                                    {group.conference.heureDebut && group.conference.heureFin && (
                                      <> de {group.conference.heureDebut} à {group.conference.heureFin}</>
                                    )}
                                  </span>
                                  <span className="conference-room">
                                    Salle: {group.conference.salle || 'Non spécifiée'}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="speakers-grid">
                            {group.speakers.map((speaker) => renderSpeakerCard(speaker))}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </>
              ) : (
                <div className="no-speakers-message">
                  <p>
                    {selectedConferenceId
                      ? "Aucun intervenant lié à cette conférence."
                      : "Aucun intervenant disponible pour le moment."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <SpeakersOnline />
    </div>
  );
}

export default Speakers;

